import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from './msalConfig';
import { useUserProfile } from './UserProfileContext';
import LinkedNotes from './components/LinkedNotes';

const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIST_NAME = 'PostItBoard';
const GRAPH = 'https://graph.microsoft.com/v1.0';

function PostItBoard() {
  const { instance, accounts } = useMsal();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showEventPicker, setShowEventPicker] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEventTitle, setSelectedEventTitle] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const listBaseRef = useRef(null);
  const [selectedNote, setSelectedNote] = useState(null);

  const { nickname } = useUserProfile();
  const userName = nickname || accounts[0]?.name || '';

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({
      ...graphRequest,
      account,
    });
    return response.accessToken;
  }, [instance, accounts]);

  const getListBase = useCallback(async (token) => {
    if (listBaseRef.current) return listBaseRef.current;

    const siteRes = await fetch(
      `${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!siteRes.ok) {
      const body = await siteRes.text();
      throw new Error(`Failed to resolve SharePoint site (${siteRes.status}): ${body}`);
    }
    const site = await siteRes.json();
    const siteId = site.id;

    const listsRes = await fetch(
      `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=100`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!listsRes.ok) {
      const body = await listsRes.text();
      throw new Error(`Failed to query lists (${listsRes.status}): ${body}`);
    }
    const listsData = await listsRes.json();
    const allLists = listsData.value || [];
    const target = LIST_NAME.toLowerCase();
    const list = allLists.find(l => l.displayName.toLowerCase() === target);
    if (!list) {
      const listNames = allLists.map(l => `"${l.displayName}"`).join(', ');
      throw new Error(
        `List "${LIST_NAME}" not found. Available lists: ${listNames}`
      );
    }

    listBaseRef.current = `${GRAPH}/sites/${siteId}/lists/${list.id}`;
    return listBaseRef.current;
  }, []);

  const fetchNotes = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const base = await getListBase(token);

      const res = await fetch(
        `${base}/items?$expand=fields&$top=500`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to load notes (${res.status}): ${body}`);
      }
      const data = await res.json();

      const notesWithAttachments = await Promise.all(
        (data.value || []).map(async (item) => {
          let thumbnailUrl = null;
          try {
            const attRes = await fetch(
              `${base}/items/${item.id}/driveItem/children`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (attRes.ok) {
              const attData = await attRes.json();
              const imgFile = (attData.value || []).find(f =>
                /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(f.name)
              );
              if (imgFile) {
                const thumbRes = await fetch(
                  `${base}/items/${item.id}/driveItem/children/${imgFile.id}/thumbnails`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (thumbRes.ok) {
                  const thumbData = await thumbRes.json();
                  thumbnailUrl = thumbData.value?.[0]?.medium?.url || null;
                }
                if (!thumbnailUrl) {
                  thumbnailUrl = imgFile['@microsoft.graph.downloadUrl'] || null;
                }
              }
            }
          } catch {
            // attachment fetch failed — not critical
          }

          return {
            id: item.id,
            title: item.fields?.Title || '',
            noteText: item.fields?.NoteText || '',
            processed: item.fields?.Processed || false,
            postedBy: item.createdBy?.user?.displayName || '',
            created: item.createdDateTime || '',
            thumbnailUrl,
          };
        })
      );

      setNotes(notesWithAttachments);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, getListBase]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  async function fetchUpcomingEvents() {
    try {
      const token = await getToken();
      // Find Family calendar
      const calRes = await fetch(`${GRAPH}/me/calendars?$select=name,id&$top=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!calRes.ok) return;
      const calData = await calRes.json();
      const familyCal = (calData.value || []).find(c => c.name.toLowerCase() === 'family');
      if (!familyCal) return;

      const now = new Date();
      const twoWeeks = new Date(now);
      twoWeeks.setDate(twoWeeks.getDate() + 14);
      const evRes = await fetch(
        `${GRAPH}/me/calendars/${familyCal.id}/calendarView?startDateTime=${now.toISOString()}&endDateTime=${twoWeeks.toISOString()}&$select=id,subject,start&$orderby=start/dateTime&$top=30`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Prefer: 'outlook.timezone="America/Chicago"',
          },
        }
      );
      if (!evRes.ok) return;
      const evData = await evRes.json();
      setUpcomingEvents((evData.value || []).map(ev => ({
        id: ev.id,
        title: ev.subject || '',
        start: ev.start?.dateTime ? new Date(ev.start.dateTime) : null,
      })));
    } catch { /* non-critical */ }
  }

  function autoTitle(text) {
    const words = text.trim().split(/\s+/).slice(0, 6).join(' ');
    return words.length > 50 ? words.substring(0, 50) + '...' : words;
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const token = await getToken();
      const base = await getListBase(token);

      const fields = {
        Title: autoTitle(noteText),
        NoteText: noteText.trim(),
        Processed: false,
      };
      if (selectedEventId) fields.EventLink = selectedEventId;

      const res = await fetch(`${base}/items`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fields }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to add note (${res.status}): ${body}`);
      }
      const created = await res.json();

      if (photo) {
        const fileName = photo.name || 'photo.jpg';
        await fetch(
          `${base}/items/${created.id}/driveItem:/${fileName}:/content`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': photo.type || 'application/octet-stream',
            },
            body: photo,
          }
        );
      }

      setNoteText('');
      setPhoto(null);
      setSelectedEventId('');
      setSelectedEventTitle('');
      const fileInput = document.getElementById('postit-photo-input');
      if (fileInput) fileInput.value = '';
      await fetchNotes();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  async function toggleProcessed(note) {
    try {
      const token = await getToken();
      const base = await getListBase(token);
      const res = await fetch(
        `${base}/items/${note.id}/fields`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ Processed: !note.processed }),
        }
      );
      if (!res.ok) throw new Error(`Failed to update note (${res.status})`);
      setNotes(prev =>
        prev.map(n => (n.id === note.id ? { ...n, processed: !n.processed } : n))
      );
      setSelectedNote(prev =>
        prev?.id === note.id ? { ...prev, processed: !note.processed } : prev
      );
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteNote(note) {
    try {
      const token = await getToken();
      const base = await getListBase(token);
      const res = await fetch(
        `${base}/items/${note.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error(`Failed to delete note (${res.status})`);
      setNotes(prev => prev.filter(n => n.id !== note.id));
    } catch (err) {
      setError(err.message);
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  if (loading) {
    return <div className="PostIt"><p className="PostIt-loading">Loading notes...</p></div>;
  }

  const active = notes.filter(n => !n.processed);
  const processed = notes.filter(n => n.processed);
  const sortedNotes = [...active, ...processed];

  return (
    <div className="PostIt">
      <h1 className="PostIt-title">Post-It Board</h1>

      {error && <p className="PostIt-error">{error}</p>}

      <form className="PostIt-form" onSubmit={handleAdd}>
        <textarea
          className="PostIt-textarea"
          placeholder="Write a note..."
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          rows={3}
          maxLength={500}
          required
        />
        <div className="PostIt-form-row">
          <label className="PostIt-photo-label" htmlFor="postit-photo-input">
            {photo ? photo.name : 'Add Photo'}
          </label>
          <input
            id="postit-photo-input"
            className="PostIt-photo-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={e => setPhoto(e.target.files[0] || null)}
          />
          <button
            type="button"
            className="PostIt-event-link-btn"
            onClick={async () => { await fetchUpcomingEvents(); setShowEventPicker(true); }}
          >
            {selectedEventTitle || 'Link to Event'}
          </button>
          <span className="PostIt-form-user">Posting as {userName}</span>
          <button className="PostIt-add-btn" type="submit" disabled={adding}>
            {adding ? 'Posting...' : 'Post'}
          </button>
        </div>
      </form>

      {showEventPicker && (
        <div className="PostIt-event-picker-overlay" onClick={() => setShowEventPicker(false)}>
          <div className="PostIt-event-picker" onClick={e => e.stopPropagation()}>
            <h3>Link to an event</h3>
            {upcomingEvents.length === 0 && <p style={{ color: '#999', fontSize: '0.9rem' }}>No upcoming events found.</p>}
            {upcomingEvents.map(ev => (
              <button
                key={ev.id}
                className="PostIt-event-picker-item"
                onClick={() => {
                  setSelectedEventId(ev.id);
                  setSelectedEventTitle(ev.title);
                  setShowEventPicker(false);
                }}
              >
                <span>{ev.title}</span>
                <span className="PostIt-event-picker-date">
                  {ev.start ? ev.start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                </span>
              </button>
            ))}
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
              {selectedEventId && (
                <button className="PostIt-event-picker-clear" onClick={() => { setSelectedEventId(''); setSelectedEventTitle(''); setShowEventPicker(false); }}>Clear Link</button>
              )}
              <button className="PostIt-event-picker-cancel" onClick={() => setShowEventPicker(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {sortedNotes.length === 0 && (
        <p className="PostIt-empty">No notes yet. Post one above!</p>
      )}

      <div className="PostIt-grid">
        {sortedNotes.map(note => (
          <div
            key={note.id}
            className={`PostIt-card${note.processed ? ' PostIt-card--processed' : ''}`}
            onClick={() => setSelectedNote(note)}
            style={{ cursor: 'pointer' }}
          >
            <div className="PostIt-card-top" onClick={e => e.stopPropagation()}>
              <span className="PostIt-card-date">{formatDate(note.created)}</span>
              <button
                className="PostIt-card-delete"
                onClick={() => deleteNote(note)}
                aria-label="Delete note"
              >
                &times;
              </button>
            </div>
            <p className="PostIt-card-text">{note.noteText}</p>
            {note.thumbnailUrl && (
              <img
                className="PostIt-card-thumb"
                src={note.thumbnailUrl}
                alt={note.title}
              />
            )}
            <div className="PostIt-card-bottom" onClick={e => e.stopPropagation()}>
              <span className="PostIt-card-author">{note.postedBy}</span>
              <button
                className={`PostIt-card-process-btn${note.processed ? ' PostIt-card-process-btn--active' : ''}`}
                onClick={() => toggleProcessed(note)}
              >
                {note.processed ? 'Undo' : 'Done'}
              </button>
            </div>
          </div>
        ))}
      </div>
      {selectedNote && (
        <div
          style={{
            position:   'fixed',
            inset:      0,
            background: 'rgba(0,0,0,0.45)',
            display:    'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex:     1000,
          }}
          onClick={() => setSelectedNote(null)}
        >
          <div
            style={{
              background:    '#fff',
              borderRadius:  '16px 16px 0 0',
              width:         '100%',
              maxWidth:      540,
              maxHeight:     '85vh',
              overflowY:     'auto',
              padding:       '20px 16px 36px',
              boxSizing:     'border-box',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span className="PostIt-card-date">{formatDate(selectedNote.created)}</span>
              <button
                onClick={() => setSelectedNote(null)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 20, color: '#aaa', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {/* Note content */}
            <p style={{ fontSize: 15, lineHeight: 1.55, margin: '0 0 10px', whiteSpace: 'pre-wrap', color: '#222' }}>
              {selectedNote.noteText}
            </p>
            {selectedNote.thumbnailUrl && (
              <img
                className="PostIt-card-thumb"
                src={selectedNote.thumbnailUrl}
                alt=""
                style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }}
              />
            )}
            <div className="PostIt-card-bottom" style={{ marginBottom: 0 }}>
              <span className="PostIt-card-author">{selectedNote.postedBy}</span>
              <button
                className={`PostIt-card-process-btn${selectedNote.processed ? ' PostIt-card-process-btn--active' : ''}`}
                onClick={() => toggleProcessed(selectedNote)}
              >
                {selectedNote.processed ? 'Undo' : 'Done'}
              </button>
            </div>

            {/* Notes divider */}
            <hr style={{ margin: '16px 0 14px', border: 'none', borderTop: '1px solid #eee' }} />

            <LinkedNotes
              itemType="PostIt"
              itemId={selectedNote.id}
              itemTitle={selectedNote.noteText}
              itemUrl={null}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PostItBoard;
